/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function BookingCard({
	image = "",
	icon = null,
	title = "",
	description = "",
	price = "",
	location = "",
	action = null,
	disableHover = false
}) {
	const hoverable = action && !disableHover;
	return (
		<Card
			sx={{
				height: "100%",
				"&:hover .card-header": {
					transform: hoverable && "translate3d(0, -50px, 0)"
				}
			}}
		>
			{icon ? (
				// Small pictogram header used instead of a full-width image
				<MDBox
					className="card-header"
					display="flex"
					justifyContent="center"
					alignItems="center"
					variant="gradient"
					bgColor="light"
					borderRadius="md"
					shadow="md"
					mx={1}
					mt={1}
					height="8rem"
					position="relative"
					zIndex={1}
					sx={{
						transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)"
					}}
				>
					{icon}
				</MDBox>
			) : (
				<MDBox
					position="relative"
					borderRadius="md"
					mx={1}
					mt={1}
					className="card-header"
					sx={{
						transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)"
					}}
				>
					<MDBox
						component="img"
						src={image}
						alt={title}
						borderRadius="md"
						shadow="md"
						width="100%"
						height="100%"
						position="relative"
						zIndex={1}
						sx={{ display: "block", aspectRatio: "4 / 3", objectFit: "cover" }}
					/>
					<MDBox
						borderRadius="md"
						shadow="md"
						width="100%"
						height="100%"
						position="absolute"
						left={0}
						top="0"
						sx={{
							backgroundImage: `url(${image})`,
							transform: "scale(0.94)",
							filter: "blur(12px)",
							backgroundSize: "cover"
						}}
					/>
				</MDBox>
			)}
			<MDBox pt={3} px={3} flexGrow={1}>
				<MDBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					mt={action ? -8 : -4.25}
				>
					{action}
				</MDBox>
				<MDTypography variant="h5" fontWeight="bold" sx={{ mt: 4 }}>
					{title}
				</MDTypography>
				<MDTypography
					component="div"
					variant="body2"
					fontWeight="regular"
					color="text"
					sx={{ mt: 1.5, mb: 1, fontSize: "14px" }}
				>
					{description}
				</MDTypography>
			</MDBox>
			<Divider />
			<MDBox
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				px={2}
				pb={1.5}
				lineHeight={1}
			>
				<MDBox color="text" display="flex" alignItems="center">
					{location}
				</MDBox>
				{price && (
					<MDTypography component="div" variant="body2" fontWeight="medium">
						{price}
					</MDTypography>
				)}
			</MDBox>
		</Card>
	);
}

// Typechecking props for the BookingCard
BookingCard.propTypes = {
	image: PropTypes.string,
	icon: PropTypes.node,
	title: PropTypes.string.isRequired,
	description: PropTypes.node,
	price: PropTypes.node,
	location: PropTypes.node,
	action: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
	disableHover: PropTypes.bool
};

export default BookingCard;
